/**
 * DateFilter Component
 * Date range picker for filtering OKRs by date
 */

import React from 'react';
import { Calendar, RefreshCcw } from 'lucide-react';

interface Cycle {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
}

interface DateFilterProps {
  startDate: string;
  endDate: string;
  onChange: (startDate: string, endDate: string) => void;
  availableRange: { min: string | null; max: string | null };
  selectedCycles?: Cycle[];
}

export const DateFilter: React.FC<DateFilterProps> = ({
  startDate,
  endDate,
  onChange,
  availableRange,
  selectedCycles,
}) => {
  // Calculate the combined date range from selected cycles
  const getCycleDateRange = () => {
    if (!selectedCycles || selectedCycles.length === 0) return null;

    const startDates = selectedCycles.map((c) => new Date(c.startDate).getTime());
    const endDates = selectedCycles.map((c) => new Date(c.endDate).getTime());

    return {
      start: new Date(Math.min(...startDates)).toISOString().split('T')[0],
      end: new Date(Math.max(...endDates)).toISOString().split('T')[0],
    };
  };

  const cycleDateRange = getCycleDateRange();

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-stone-700">
        Date Range
      </label>

      <div className="space-y-3">
        {/* Start Date */}
        <div className="space-y-1">
          <label className="block text-xs text-stone-600">Start Date</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Calendar className="h-4 w-4 text-stone-400" />
            </div>
            <input
              type="date"
              value={startDate}
              onChange={(e) => onChange(e.target.value, endDate)}
              min={availableRange.min || undefined}
              max={endDate || availableRange.max || undefined}
              className="block w-full pl-10 pr-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
            />
          </div>
        </div>

        {/* End Date */}
        <div className="space-y-1">
          <label className="block text-xs text-stone-600">End Date</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Calendar className="h-4 w-4 text-stone-400" />
            </div>
            <input
              type="date"
              value={endDate}
              onChange={(e) => onChange(startDate, e.target.value)}
              min={startDate || availableRange.min || undefined}
              max={availableRange.max || undefined}
              className="block w-full pl-10 pr-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
            />
          </div>
        </div>
      </div>

      {/* Quick Select Presets */}
      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={() => {
            const today = new Date();
            const quarterStart = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1);
            const quarterEnd = new Date(quarterStart.getFullYear(), quarterStart.getMonth() + 3, 0);
            onChange(
              quarterStart.toISOString().split('T')[0],
              quarterEnd.toISOString().split('T')[0]
            );
          }}
          className="px-3 py-1.5 text-xs font-medium text-primary-600 bg-primary-50 border border-primary-200 rounded hover:bg-primary-100 transition-colors"
        >
          This Quarter
        </button>
        <button
          onClick={() => {
            const today = new Date();
            const yearStart = new Date(today.getFullYear(), 0, 1);
            const yearEnd = new Date(today.getFullYear(), 11, 31);
            onChange(
              yearStart.toISOString().split('T')[0],
              yearEnd.toISOString().split('T')[0]
            );
          }}
          className="px-3 py-1.5 text-xs font-medium text-primary-600 bg-primary-50 border border-primary-200 rounded hover:bg-primary-100 transition-colors"
        >
          This Year
        </button>
      </div>

      {/* Sync with Cycle */}
      {cycleDateRange && (
        <button
          onClick={() => onChange(cycleDateRange.start, cycleDateRange.end)}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium text-purple-600 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors"
        >
          <RefreshCcw className="w-3.5 h-3.5" />
          Use Cycle Dates ({selectedCycles?.length === 1 ? selectedCycles[0].name : `${selectedCycles?.length} cycles`})
        </button>
      )}

      {/* Available Range Info */}
      {availableRange.min && availableRange.max && (
        <div className="text-xs text-stone-500 text-center">
          Available: {new Date(availableRange.min).toLocaleDateString()} - {new Date(availableRange.max).toLocaleDateString()}
        </div>
      )}
    </div>
  );
};
